import { forwardRef, useEffect, useRef, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { Link } from "react-router-dom";
import { Trophy, Camera, InstagramLogo, Medal, Certificate, Crown } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { downloadImage } from "@/components/exam/CertificatePanel";
import GaleriaShareButton from "@/components/GaleriaShareButton";

export const monthName = (m, lang) => {
  const s = new Date(`${m}-15T12:00:00Z`).toLocaleDateString(lang === "es" ? "es" : "en", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const ChampionSquare = forwardRef(function ChampionSquare({ champ, lang }, ref) {
  const es = lang === "es";
  return (
    <div ref={ref} style={{ width: 1080, height: 1080 }} className="relative bg-[#0A0A0A] text-white overflow-hidden font-['Outfit']">
      <div className="absolute inset-8 border-[3px] border-[#F5A524] rounded-xl" />
      <div className="absolute inset-12 border border-[#F5A524]/30 rounded-lg" />
      <div className="relative h-full flex flex-col items-center justify-center text-center px-28">
        <div className="flex items-center gap-3 leading-none">
          <Camera weight="fill" className="w-12 h-12 text-[#F5A524]" />
          <span className="text-4xl font-semibold tracking-tight leading-none">DSLR Advisor</span>
        </div>
        <Trophy weight="fill" className="mt-14 w-44 h-44 text-[#F5A524]" />
        <p className="mt-10 text-[#F5A524] uppercase tracking-[0.3em] text-2xl font-semibold">{es ? "Campeón de la Clase" : "Class Champion"}</p>
        <p className="mt-4 text-[#D4D4D8] text-3xl">{monthName(champ.month, lang)}</p>
        <p className="mt-10 font-semibold tracking-tight leading-tight" style={{ fontSize: champ.display.length > 16 ? 72 : 92 }}>{champ.display}</p>
        <div className="mt-10 flex items-center gap-4 bg-[#F5A524]/10 border border-[#F5A524]/40 rounded-2xl px-10 h-24">
          <span className="font-['JetBrains_Mono'] text-5xl text-[#F5A524] h-12 inline-flex items-center" style={{ lineHeight: "48px" }}>{champ.badges}</span>
          <span className="text-[#D4D4D8] text-2xl h-12 inline-flex items-center" style={{ lineHeight: "48px" }}>{es ? "insignias este mes" : "badges this month"}</span>
        </div>
        <p className="mt-12 text-[#71717A] text-xl">#DSLRAdvisor · {window.location.host}</p>
      </div>
    </div>
  );
});

function MyTitle({ champ, lang }) {
  const es = lang === "es";
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const canvas = await html2canvas(ref.current, { scale: 1, width: 1080, height: 1080, backgroundColor: "#0A0A0A" });
      canvas.toBlob((b) => b && downloadImage(b, `dslr-advisor-champion-${champ.month}.png`), "image/png");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap bg-[#F5A524]/10 border border-[#F5A524]/50 rounded-xl p-4" data-testid={`champion-mine-${champ.month}`}>
      <p className="flex items-center gap-2 text-white font-medium">
        <Trophy weight="fill" className="w-5 h-5 text-[#F5A524]" />
        {es ? `¡Eres el Campeón de la Clase de ${monthName(champ.month, lang)}!` : `You're the Class Champion of ${monthName(champ.month, lang)}!`}
      </p>
      <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
        <Button onClick={save} disabled={busy} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium" data-testid={`champion-post-${champ.month}`}>
          <InstagramLogo weight="fill" className="w-4 h-4 mr-1.5" />
          {busy ? (es ? "Generando..." : "Generating...") : es ? "Imagen para publicar" : "Ready-to-post image"}
        </Button>
        <GaleriaShareButton testId={`champion-share-${champ.month}`} />
      </div>
      <div aria-hidden style={{ position: "fixed", left: -12000, top: 0, pointerEvents: "none" }}>
        <ChampionSquare ref={ref} champ={champ} lang={lang} />
      </div>
    </div>
  );
}

function ChampTile({ label, champ, lang, testId }) {
  const es = lang === "es";
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 flex-1 min-w-[220px]" data-testid={testId}>
      <p className="text-[#71717A] text-[10px] uppercase tracking-[0.18em]">{label}</p>
      {champ ? (
        <>
          <p className="text-white font-medium mt-1 flex items-center gap-1.5">
            {champ.display}
            {champ.is_me && <span className="text-[#F5A524] text-xs">{es ? "(tú)" : "(you)"}</span>}
          </p>
          <p className="flex items-center gap-3 text-xs mt-1 font-['JetBrains_Mono']">
            <span className="flex items-center gap-1 text-emerald-300"><Medal weight="fill" className="w-3.5 h-3.5" />{champ.badges}</span>
            <span className="flex items-center gap-1 text-[#F5A524]"><Certificate weight="fill" className="w-3.5 h-3.5" />{champ.best_exam || "-"}</span>
          </p>
        </>
      ) : (
        <p className="text-[#52525B] text-sm mt-1">{es ? "Aún nadie" : "No one yet"}</p>
      )}
    </div>
  );
}

export default function ChampionCard() {
  const { lang } = useTranslation();
  const es = lang === "es";
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get("/api/champion", { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const now = new Date();
  const curMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  const prevName = data.previous ? monthName(data.previous.month, lang) : "";

  return (
    <div className="bg-gradient-to-br from-[#F5A524]/10 via-[#121214] to-[#121214] border border-[#F5A524]/30 rounded-xl p-4 sm:p-5 space-y-3" data-testid="champion-card">
      <div>
        <h2 className="flex items-center gap-2 font-['Outfit'] text-white text-lg font-semibold">
          <Trophy weight="fill" className="w-5 h-5 text-[#F5A524]" />
          {es ? "Campeón Mensual de la Clase" : "Monthly Class Champion"}
        </h2>
        <p className="text-[#71717A] text-sm">
          {es ? "Quien gana más insignias en el mes (entre quienes aparecen en la tabla). Desempata el mejor examen del mes." : "Whoever earns the most badges in a month (among students on the leaderboard). Best exam that month breaks ties."}
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <ChampTile label={es ? (prevName ? `Campeón de ${prevName}` : "Campeón del mes pasado") : `Champion of ${prevName || "last month"}`} champ={data.previous} lang={lang} testId="champion-previous" />
        <ChampTile label={es ? `Liderando ${monthName(curMonth, lang)}` : `Leading ${monthName(curMonth, lang)}`} champ={data.current} lang={lang} testId="champion-current" />
      </div>
      <Link to="/hall-of-fame" className="inline-flex items-center gap-1.5 text-sm text-[#F5A524] hover:underline" data-testid="champion-hall-link">
        <Crown weight="fill" className="w-4 h-4" />
        {es ? "Ver el Salón de la Fama" : "See the Hall of Fame"}
      </Link>
      {data.my_titles.map((c) => (
        <MyTitle key={c.month} champ={c} lang={lang} />
      ))}
    </div>
  );
}
